<template>
  <div class="d-flex align-items-center gap-2">
    <GuSelect
      v-model="selectedPresetId"
      class="preset-select"
      aria-label="Selected Preset"
      placeholder="No preset selected"
      @update:model-value="setSelectedPresetId"
    >
      <GuSelectGroup
        v-for="[levelKey, levelValue] in Object.entries(PRESET_SHARE_LEVEL)"
        :key="levelKey"
        :label="levelValue"
      >
        <GuSelectItem
          v-if="(organizePresetsByType()[levelValue] || []).length === 0"
          disabled
          :value="`${levelValue}-disabled`"
        >
          No {{ levelValue.toLowerCase() }} presets
        </GuSelectItem>
        <GuSelectItem
          v-for="preset in organizePresetsByType()[levelValue] || []"
          :key="preset.id"
          :value="preset.id"
        >
          <i
            v-if="preset.id === favoritePreset?.preferenceValue"
            class="bx bxs-star order-1 favorite"
            aria-hidden="true"
          />

          {{ preset.name }}
        </GuSelectItem>
      </GuSelectGroup>
    </GuSelect>

    <GuButton
      v-if="selectedPresetId"
      variant="danger"
      icon="bx bx-x"
      @click.prevent="clearSelectedPresetId"
    />
  </div>
</template>

<script setup>
import { onMounted, ref } from 'vue'

import { getFavoritePresets, getPresets } from '@gu-datagrid-new/api/datagrid.api'
import GuButton from '@shared/gu-components/button/GuButton.vue'
import { GuSelect, GuSelectItem, GuSelectGroup } from '@shared/gu-components/select'

const emits = defineEmits([
  'clearPreset',
  'setPreset',
])

const CHARTS_PRESET_SCOPE = 'config.dashboard.charts.presets'

const PRESET_SHARE_LEVEL = {
  PRIVATE: 'PRIVATE',
  PUBLIC: 'PUBLIC',
  ROLE: 'ROLE',
}

const favoritePreset = ref(null)
const presets = ref([])
const selectedPresetId = ref(null)

async function fetchPresets (scope) {
  try {
    const data = await getPresets(scope)
    return data
  } catch (error) {
    console.error('fetchPresets', error, error.message)
  }
}

async function fetchFavoritePreset () {
  try {
    const response = await getFavoritePresets()
    const data = Array.isArray(response?.data) ? response.data : Array.isArray(response) ? response : []

    if (!data.length) return null

    const favoritePreset = data.find(pref => pref.preferenceKey === 'favoritePreset')
    if (!favoritePreset) return null

    const value = Number(favoritePreset.preferenceValue)

    return {
      ...favoritePreset,
      preferenceValue: value,
    }
  } catch (err) {
    console.error('fetchFavoritePreset', err, err.message)
  }
}

function setSelectedPresetId (presetId) {
  selectedPresetId.value = presetId || null
  sessionStorage.setItem('dashboard-charts-preset-id', presetId)
  emits('setPreset', presets.value.find(preset => preset.id === presetId) || null)
}

function clearSelectedPresetId () {
  selectedPresetId.value = null
  sessionStorage.removeItem('dashboard-charts-preset-id')
  emits('clearPreset')
}

function organizePresetsByType () {
  return presets.value.reduce((acc, preset) => {
    acc[preset.shareLevel] = acc[preset.shareLevel] || []
    acc[preset.shareLevel].push(preset)
    return acc
  }, {})
}

onMounted(async () => {
  const PRESETS_DATA = await fetchPresets(CHARTS_PRESET_SCOPE)
  presets.value = PRESETS_DATA

  const FAVORITE_PRESET_DATA = await fetchFavoritePreset()
  favoritePreset.value = FAVORITE_PRESET_DATA

  const SESSION_PRESET_ID = Number(sessionStorage.getItem('dashboard-charts-preset-id'))

  if (SESSION_PRESET_ID && PRESETS_DATA.find(preset => preset.id === SESSION_PRESET_ID)) {
    setSelectedPresetId(SESSION_PRESET_ID)
  }

  if (!SESSION_PRESET_ID && FAVORITE_PRESET_DATA && PRESETS_DATA.find(preset => preset.id === FAVORITE_PRESET_DATA.preferenceValue)) {
    setSelectedPresetId(FAVORITE_PRESET_DATA.preferenceValue)
  }
})
</script>

<style lang="scss" scoped>
i.bx.bxs-star.favorite {
  color: #FFD700;
  position: static;
  right: auto;
  bottom: auto;
  font-size: inherit;
  transform: none;
}

:deep(.btn) {
  i.bx.bx-x {
    position: static !important;
    right: auto !important;
    bottom: auto !important;
    font-size: inherit !important;
    transform: none !important;
    color: inherit !important;
  }
}
</style>
